const MN_FREE_GIFT_DISMISSED_PREFIX = "mn-free-gift-dismissed:";
const MN_BUNDLE_PARENT_HANDLE = "erstelle-dein-bundle";
const MN_BUNDLE_GIFT_HANDLE = "hornloffel-gratis-geschenk";
const MN_BUNDLE_BOX_PROPERTY = "Box";
let mnCartGiftMutationInProgress = false;

function clearFreeGiftDismissals() {
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);

      if (key?.startsWith(MN_FREE_GIFT_DISMISSED_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch (_error) {
    // The gift still works if storage is unavailable; only the dismissal memory is lost.
  }
}

function collectCartSections() {
  const sections = new Set();

  document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
    bubbles: true,
    detail: { sections }
  }));

  return [...sections];
}

class MnFreeGiftController extends HTMLElement {
  connectedCallback() {
    this.variantId = Number(this.dataset.variantId);
    this.threshold = Number(this.dataset.threshold);
    this.cartValue = Number(this.dataset.cartValue);
    this.giftLineKey = this.dataset.giftLineKey;
    this.giftQuantity = Number(this.dataset.giftQuantity || 0);
    this.giftAvailable = this.dataset.giftAvailable === "true";
    this.dismissedKey = `${MN_FREE_GIFT_DISMISSED_PREFIX}${this.variantId}`;
    this.onLineItemWillChange = this.onLineItemWillChange.bind(this);

    document.addEventListener("line-item:will-change", this.onLineItemWillChange);
    queueMicrotask(() => this.synchronizeGift());
  }

  disconnectedCallback() {
    document.removeEventListener("line-item:will-change", this.onLineItemWillChange);
  }

  onLineItemWillChange(event) {
    const lineItem = event.target.closest?.("line-item");

    if (Number(lineItem?.dataset.variantId) !== this.variantId || Number(event.detail?.targetQuantity) !== 0) {
      return;
    }

    try {
      sessionStorage.setItem(this.dismissedKey, "true");
    } catch (_error) {
      // Storage can be unavailable in privacy modes. Removal itself must still work.
    }
  }

  wasDismissed() {
    try {
      return sessionStorage.getItem(this.dismissedKey) === "true";
    } catch (_error) {
      return false;
    }
  }

  clearDismissal() {
    try {
      sessionStorage.removeItem(this.dismissedKey);
    } catch (_error) {
      // Storage can be unavailable in privacy-restricted browsers.
    }
  }

  async synchronizeGift() {
    if (!this.variantId || !this.threshold || mnCartGiftMutationInProgress) {
      return;
    }

    const isEligible = this.cartValue >= this.threshold;

    if (!isEligible) {
      this.clearDismissal();

      if (this.giftLineKey) {
        await this.changeGiftQuantity(0);
      }

      return;
    }

    if (isEligible && this.giftLineKey && this.giftQuantity !== 1) {
      await this.changeGiftQuantity(1);
      return;
    }

    if (isEligible && !this.giftLineKey && this.giftAvailable && !this.wasDismissed()) {
      await this.addGift();
    }
  }

  async addGift() {
    const sections = collectCartSections();

    await this.mutateCart(`${Shopify.routes.root}cart/add.js`, {
      items: [{
        id: this.variantId,
        quantity: 1,
        properties: { _mn_free_gift: "true" }
      }],
      sections: sections.join(","),
      sections_url: window.location.pathname
    }, true);
  }

  async changeGiftQuantity(quantity) {
    const sections = collectCartSections();

    await this.mutateCart(`${Shopify.routes.root}cart/change.js`, {
      id: this.giftLineKey,
      quantity,
      sections: sections.join(","),
      sections_url: window.location.pathname
    }, false);
  }

  async mutateCart(url, payload, isAddRequest) {
    mnCartGiftMutationInProgress = true;
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify(payload)
      });
      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || responseJson.message || "The free gift could not be updated.");
      }

      if (window.themeVariables?.settings?.pageType === "cart") {
        window.location.reload();
        return;
      }

      const cartContent = isAddRequest
        ? await (await fetch(`${Shopify.routes.root}cart.js`)).json()
        : responseJson;

      if (responseJson.sections) {
        cartContent.sections = responseJson.sections;
      }

      if (cartContent.sections) {
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "mn-free-gift:sync",
            cart: cartContent
          }
        }));
      } else {
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      }
    } catch (error) {
      console.error("Mother Nurture free gift:", error);
    } finally {
      mnCartGiftMutationInProgress = false;
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
  }
}

if (!window.customElements.get("mn-free-gift-controller")) {
  window.customElements.define("mn-free-gift-controller", MnFreeGiftController);
}

class MnBundleGiftController extends HTMLElement {
  connectedCallback() {
    this.pendingCart = null;
    this.syncTimer = null;
    this.onCartChange = this.onCartChange.bind(this);
    this.onCartRefresh = this.onCartRefresh.bind(this);

    document.addEventListener("cart:change", this.onCartChange);
    document.addEventListener("cart:refresh", this.onCartRefresh);
    queueMicrotask(() => this.scheduleSynchronization());
  }

  disconnectedCallback() {
    document.removeEventListener("cart:change", this.onCartChange);
    document.removeEventListener("cart:refresh", this.onCartRefresh);
    clearTimeout(this.syncTimer);
  }

  onCartChange(event) {
    if (event.detail?.baseEvent === "mn-bundle-gift:sync") {
      return;
    }

    this.scheduleSynchronization(event.detail?.cart);
  }

  onCartRefresh() {
    this.scheduleSynchronization();
  }

  scheduleSynchronization(cart = null, delay = 60) {
    if (cart?.items) {
      this.pendingCart = cart;
    }

    clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => this.synchronizeGifts(), delay);
  }

  buildUpdates(items) {
    const parentQuantitiesByBox = new Map();
    const giftLinesByBox = new Map();

    for (const item of items || []) {
      const boxValue = item.properties?.[MN_BUNDLE_BOX_PROPERTY];
      const boxId = boxValue == null ? "" : String(boxValue).trim();

      if (!boxId) {
        continue;
      }

      const quantity = Math.max(0, Number(item.quantity) || 0);

      if (item.handle === MN_BUNDLE_PARENT_HANDLE) {
        parentQuantitiesByBox.set(boxId, (parentQuantitiesByBox.get(boxId) || 0) + quantity);
      } else if (item.handle === MN_BUNDLE_GIFT_HANDLE && item.key) {
        const giftLines = giftLinesByBox.get(boxId) || [];
        giftLines.push({ key: item.key, quantity });
        giftLinesByBox.set(boxId, giftLines);
      }
    }

    const updates = {};

    for (const [boxId, giftLines] of giftLinesByBox) {
      let remainingGiftQuantity = parentQuantitiesByBox.get(boxId) || 0;

      for (const giftLine of giftLines) {
        const targetQuantity = Math.min(giftLine.quantity, remainingGiftQuantity);
        remainingGiftQuantity -= targetQuantity;

        if (targetQuantity !== giftLine.quantity) {
          updates[giftLine.key] = targetQuantity;
        }
      }

      if (remainingGiftQuantity > 0 && giftLines.length > 0) {
        const firstGiftLine = giftLines[0];
        const currentTargetQuantity = Object.hasOwn(updates, firstGiftLine.key)
          ? updates[firstGiftLine.key]
          : firstGiftLine.quantity;

        updates[firstGiftLine.key] = currentTargetQuantity + remainingGiftQuantity;
      }
    }

    return updates;
  }

  async synchronizeGifts() {
    if (mnCartGiftMutationInProgress) {
      this.scheduleSynchronization(this.pendingCart, 120);
      return;
    }

    let cartContent = this.pendingCart;
    this.pendingCart = null;

    if (!cartContent?.items) {
      try {
        const response = await fetch(`${Shopify.routes.root}cart.js`, {
          headers: { "X-Requested-With": "XMLHttpRequest" }
        });

        if (!response.ok) {
          return;
        }

        cartContent = await response.json();
      } catch (_error) {
        return;
      }
    }

    const updates = this.buildUpdates(cartContent.items);

    if (Object.keys(updates).length === 0) {
      return;
    }

    await this.updateCart(updates);
  }

  async updateCart(updates) {
    const sections = collectCartSections();
    mnCartGiftMutationInProgress = true;
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          updates,
          sections: sections.join(","),
          sections_url: window.location.pathname
        })
      });
      const cartContent = await response.json();

      if (!response.ok) {
        throw new Error(cartContent.description || cartContent.message || "The bundle gift could not be synchronized.");
      }

      if (window.themeVariables?.settings?.pageType === "cart") {
        window.location.reload();
        return;
      }

      if (cartContent.sections) {
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "mn-bundle-gift:sync",
            cart: cartContent
          }
        }));
      } else {
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      }
    } catch (error) {
      console.error("Mother Nurture bundle gift:", error);
    } finally {
      mnCartGiftMutationInProgress = false;
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
  }
}

if (!window.customElements.get("mn-bundle-gift-controller")) {
  window.customElements.define("mn-bundle-gift-controller", MnBundleGiftController);
}

document.addEventListener("cart:change", (event) => {
  if (event.detail?.cart?.item_count === 0) {
    clearFreeGiftDismissals();
  }
});

queueMicrotask(() => {
  if (!document.querySelector("mn-free-gift-controller")) {
    clearFreeGiftDismissals();
  }
});
